import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const filteredArticles = useMemo(() => {
    let result = articles.filter((a) => a.category === activeCategory);
    if (sortBy === 'hot') {
      result = [...result].sort((a, b) => b.viewCount - a.viewCount);
    }
    return result;
  }, [activeCategory, sortBy]);

  const categoryTitle = currentCategory?.name || '全部分类';
  const categoryDesc = currentCategory?.description || '浏览所有分类内容';

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${categoryTitle} - 黑料在线看`,
          description: categoryDesc,
          url: `https://heiliaozx.com/category/${activeCategory}`,
          isPartOf: { '@type': 'WebSite', name: '黑料在线看', url: 'https://heiliaozx.com' },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryTitle },
              ]}
            />

            {/* Category Header */}
            <div className="py-6 md:py-10">
              <div className="flex items-center gap-3 mb-3">
                {currentCategory && (
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10">
                    <i className={`${currentCategory.icon} text-primary-400 text-lg`}></i>
                  </div>
                )}
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-50">
                  {categoryTitle}
                </h1>
              </div>
              <p className="text-foreground-400 text-sm md:text-base max-w-2xl">
                {categoryDesc}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200 border border-background-200/50'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-foreground-500 text-sm">
                共 <strong className="text-foreground-200">{filteredArticles.length}</strong> 篇内容
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    sortBy === 'latest'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  最新
                </button>
                <button
                  onClick={() => setSortBy('hot')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    sortBy === 'hot'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  最热
                </button>
              </div>
            </div>

            {/* Article Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-inbox-line text-foreground-500 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容</p>
              </div>
            )}

            {/* BreadcrumbList Schema */}
            <SchemaOrg
              type="BreadcrumbList"
              data={{
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://heiliaozx.com/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: categoryTitle,
                  },
                ],
              }}
            />
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}